import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { Box, Divider, Typography } from "@mui/material";

import { useGetService } from "../../api/queries/useGetService";
import { useServiceFeedbackInfinite } from "../../api/queries/useServiceFeedback";
import EmptyReviewsFigure from "../../assets/images/empty-reviews.png";

import { GoHireHeader } from "../../components/GoHireHeader";
import RatingDetails from "../../components/RatingDetails";
import ServiceSummary from "../../components/ServiceSummary";
import RequestSubmittedModal from "../../components/RequestSubmittedModal";
import { ServiceFeedbackCard, ServiceFeedbackCardLoader } from "../../components/ServiceFeedbackCard";

import { serviceReviewsPageStyles } from "./styles";

const REVIEW_LIMIT = 6;

const ServicePage = () => {
  const { serviceId } = useParams();
  const { t } = useTranslation("goService", {
    keyPrefix: "serviceReviewsPage",
  });
  const { data: service, isLoading: isServiceLoading } = useGetService(Number(serviceId));

  const [isRequestSubmittedModalOpen, setIsRequestSubmittedModalOpen] = useState(false);

  const handleRequestSubmittedModal = () => setIsRequestSubmittedModalOpen(true);
  const handleCloseRequestSubmittedModal = () => setIsRequestSubmittedModalOpen(false);

  const {
    data: reviews,
    isLoading: isUserReviewsLoading,
    isError: iseUserReviewsHasError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useServiceFeedbackInfinite({
    serviceId: Number(serviceId),
    limit: REVIEW_LIMIT,
  });

  const meta = reviews?.meta;
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const isLoading = isUserReviewsLoading || isFetchingNextPage;
  const isEmpty = reviews?.items.length === 0;

  return (
    <Box sx={{ display: "flex", gap: "10px", position: "relative", flexDirection: { xs: "column", md: "row" } }}>
      <Box sx={serviceReviewsPageStyles.container}>
        <GoHireHeader
          service={service}
          isServiceLoading={isServiceLoading}
          handleRequestSubmittedModal={handleRequestSubmittedModal}
        />
        <Box sx={serviceReviewsPageStyles.reviewsSection}>
          <Typography sx={serviceReviewsPageStyles.title}>{t("title")}</Typography>
          {isEmpty && (
            <Box sx={serviceReviewsPageStyles.emptyStateContainer}>
              <Box
                component="img"
                src={EmptyReviewsFigure}
                alt="empty-reviews"
                sx={serviceReviewsPageStyles.emptyStateImage}
              />
              <Typography sx={serviceReviewsPageStyles.emptyStateText}>{t("noReviews")}</Typography>
            </Box>
          )}
          {!isEmpty && (
            <>
              <RatingDetails meta={meta} isLoading={isLoading} />
              <Divider />
              <Typography sx={serviceReviewsPageStyles.peopleReviewsTitle}>{t("peopleReviews")}</Typography>
              {reviews?.items.map((review, index) => (
                <>
                  <ServiceFeedbackCard key={review.id} feedback={review} isFullHeight={false} isFullWidth={true} />
                  {index !== reviews?.items.length - 1 && <Divider />}
                </>
              ))}
              {isLoading && [...Array(3)].map((_, index) => <ServiceFeedbackCardLoader key={index} />)}
              <div ref={ref} />
            </>
          )}
        </Box>
      </Box>
      <ServiceSummary
        service={service}
        isServiceLoading={isServiceLoading}
        handleRequestSubmittedModal={handleRequestSubmittedModal}
      />
      <RequestSubmittedModal open={isRequestSubmittedModalOpen} closeModal={handleCloseRequestSubmittedModal} />
    </Box>
  );
};

export default ServicePage;
